import { useState } from "react";
import { useTranslation } from "react-i18next";
import { formatPrice, formatHour } from "@/lib/utils";
import { Download, CreditCard } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid } from "recharts";

function todayStr() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function FinancialsTab({ bookings, earnings }) {
  const { t } = useTranslation();
  const today = todayStr();
  const weekStart = (() => { const d = new Date(); d.setDate(d.getDate() - d.getDay()); const y = d.getFullYear(); const m = String(d.getMonth() + 1).padStart(2, "0"); const day = String(d.getDate()).padStart(2, "0"); return `${y}-${m}-${day}`; })();
  const monthStart = today.slice(0, 7) + "-01";
  const yearStart = today.slice(0, 4) + "-01-01";

  const [finPeriod, setFinPeriod] = useState("month");
  const [chartRange, setChartRange] = useState(14);
  const [finDateFrom, setFinDateFrom] = useState(monthStart);
  const [finDateTo, setFinDateTo] = useState(today);

  const confirmed = bookings.filter((b) => b.status === "confirmed");

  const periodPresets = [
    { key: "today", label: "owner.today_label", from: today, to: today },
    { key: "week", label: "owner.week_label", from: weekStart, to: today },
    { key: "month", label: "owner.month_label", from: monthStart, to: today },
    { key: "year", label: "owner.year_label", from: yearStart, to: today },
  ];

  const isPresetMatch = periodPresets.some(
    (p) => p.key === finPeriod && finDateFrom === p.from && finDateTo === p.to
  );

  const periodStart = !isPresetMatch && (finDateFrom || finDateTo)
    ? finDateFrom || ""
    : finPeriod === "today" ? today : finPeriod === "week" ? weekStart : finPeriod === "month" ? monthStart : finPeriod === "year" ? yearStart : "";
  const periodEnd = !isPresetMatch && finDateTo ? finDateTo : today;
  const periodBookings = confirmed.filter((b) => b.date >= periodStart && b.date <= periodEnd);
  const periodRevenue = periodBookings.reduce((s, b) => s + (b.total_price || 0), 0);
  const periodCount = periodBookings.length;
  const periodAvg = periodCount > 0 ? periodRevenue / periodCount : 0;
  const pendingCOD = confirmed
    .filter((b) => b.payment_status === "pending_cod" && b.date <= today)
    .reduce((s, b) => s + (b.total_price || 0), 0);

  const chartData = earnings.series.length > 0
    ? chartRange === "all" ? earnings.series : earnings.series.slice(-chartRange)
    : [];

  const byCourt = {};
  periodBookings.forEach((b) => {
    const name = b.court_name || "Unknown";
    if (!byCourt[name]) byCourt[name] = { revenue: 0, count: 0 };
    byCourt[name].revenue += b.total_price || 0;
    byCourt[name].count += 1;
  });
  const courtRows = Object.entries(byCourt).sort(([, a], [, b]) => b.revenue - a.revenue);

  const byPayment = {};
  periodBookings.forEach((b) => {
    const key = b.payment_method || "unknown";
    byPayment[key] = (byPayment[key] || 0) + (b.total_price || 0);
  });
  const paymentRows = Object.entries(byPayment).sort(([, a], [, b]) => b - a);
  const paymentTotal = paymentRows.reduce((s, [, v]) => s + v, 0);

  const exportCsv = () => {
    const header = ["Court", "Date", "Time", "Player", "Amount", "Payment Method", "Status"];
    const rows = periodBookings.map((b) => [
      b.court_name || "",
      b.date || "",
      `${formatHour(b.start_hour)} - ${formatHour(b.end_hour)}`,
      b.user_name || "",
      b.total_price || 0,
      b.payment_method || "",
      b.status || "",
    ]);
    const csvContent = [header, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const d = new Date();
    a.download = `financials-export-${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (confirmed.length === 0) {
    return (
      <div className="border border-dashed border-border/30 p-16 text-center">
        <CreditCard className="h-8 w-8 mx-auto text-muted-foreground/40 mb-3" />
        <p className="text-muted-foreground/80 text-sm">{t("owner.no_data")}</p>
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center gap-2 mb-6 flex-wrap">
        <div className="inline-flex border border-border overflow-hidden">
          {periodPresets.map((p) => (
            <button
              key={p.key}
              onClick={() => { setFinPeriod(p.key); setFinDateFrom(p.from); setFinDateTo(p.to); }}
              className={`px-3 py-1.5 text-[10px] tracking-widest uppercase font-bold transition-all ${
                isPresetMatch && finPeriod === p.key
                  ? "bg-volt text-primary-foreground"
                  : "bg-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              {t(p.label)}
            </button>
          ))}
        </div>
        <input
          type="date"
          value={finDateFrom}
          onChange={(e) => { setFinDateFrom(e.target.value); setFinPeriod("month"); }}
          className="bg-background border border-input px-3 py-1.5 text-[10px] tracking-widest uppercase text-muted-foreground font-bold focus:outline-none focus:border-volt"
        />
        <span className="text-[10px] text-muted-foreground/60">–</span>
        <input
          type="date"
          value={finDateTo}
          onChange={(e) => { setFinDateTo(e.target.value); setFinPeriod("month"); }}
          className="bg-background border border-input px-3 py-1.5 text-[10px] tracking-widest uppercase text-muted-foreground font-bold focus:outline-none focus:border-volt"
        />
        {!isPresetMatch && (
          <button
            onClick={() => { setFinDateFrom(monthStart); setFinDateTo(today); setFinPeriod("month"); }}
            className="text-[10px] tracking-widest uppercase text-yellow-400 hover:underline font-bold"
          >
            {t("owner.clear_date")}
          </button>
        )}
        <button
          onClick={exportCsv}
          className="btn-outline rounded-sm px-2.5 py-1.5 text-[10px] tracking-widest uppercase font-bold flex items-center gap-1 ml-auto"
        >
          <Download className="h-3 w-3" /> {t("owner.export_csv")}
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-1 mb-8">
        <div className="border border-border/30 bg-card p-5">
          <div className="label-eyebrow mb-1">{t("owner.revenue_title")}</div>
          <div className="font-heading text-3xl md:text-4xl text-volt">{formatPrice(periodRevenue, "JOD")}</div>
        </div>
        <div className="border border-border/30 bg-card p-5">
          <div className="label-eyebrow mb-1">{t("owner.confirmed_bookings")}</div>
          <div className="font-heading text-3xl md:text-4xl">{periodCount}</div>
        </div>
        <div className="border border-border/30 bg-card p-5">
          <div className="label-eyebrow mb-1">{t("owner.avg_per_booking")}</div>
          <div className="font-heading text-3xl md:text-4xl text-volt">{formatPrice(periodAvg, "JOD")}</div>
        </div>
        <div className="border border-border/30 bg-card p-5">
          <div className="label-eyebrow mb-1">{t("owner.pending_cod")}</div>
          <div className="font-heading text-3xl md:text-4xl text-yellow-400">{formatPrice(pendingCOD, "JOD")}</div>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_380px] gap-6 mb-8">
        <div className="border border-border/30 bg-card p-5">
          <div className="flex items-center justify-between mb-4">
            <span className="label-eyebrow">{t("owner.earnings_trend")}</span>
            <div className="inline-flex border border-border overflow-hidden">
              {[7, 14, 30, "all"].map((r) => (
                <button
                  key={String(r)}
                  onClick={() => setChartRange(r)}
                  className={`px-2 py-1 text-[9px] tracking-widest uppercase font-bold transition-all ${
                    chartRange === r
                      ? "bg-volt text-primary-foreground"
                      : "bg-transparent text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {r === "all" ? t("owner.all_time_short") : `${r}d`}
                </button>
              ))}
            </div>
          </div>
          <div className="h-64">
            {chartData.length === 0 ? (
              <div className="h-full flex items-center justify-center text-muted-foreground/60 text-sm">
                {t("owner.no_data")}
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4" />
                  <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={10} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))" }} />
                  <Line type="monotone" dataKey="amount" stroke="#CCFF00" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <div className="border border-border/30 bg-card p-5">
            <div className="label-eyebrow mb-3">{t("owner.by_court")}</div>
            {courtRows.length === 0 ? (
              <div className="text-sm text-muted-foreground/60 py-6 text-center">{t("owner.no_data")}</div>
            ) : (
              <div className="space-y-2 max-h-52 overflow-auto">
                {courtRows.map(([name, data]) => {
                  const pct = periodRevenue > 0 ? (data.revenue / periodRevenue) * 100 : 0;
                  return (
                    <div key={name}>
                      <div className="flex justify-between text-xs mb-0.5">
                        <span className="truncate text-muted-foreground">{name}</span>
                        <span className="text-volt font-bold flex-shrink-0">{formatPrice(data.revenue, "JOD")}</span>
                      </div>
                      <div className="h-1 bg-background rounded-sm overflow-hidden">
                        <div className="h-full bg-volt rounded-sm transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="border border-border/30 bg-card p-5">
            <div className="label-eyebrow mb-3">{t("owner.by_payment")}</div>
            {paymentRows.length === 0 ? (
              <div className="text-sm text-muted-foreground/60 py-6 text-center">{t("owner.no_data")}</div>
            ) : (
              <div className="space-y-2">
                {paymentRows.map(([method, amount]) => {
                  const pct = paymentTotal > 0 ? (amount / paymentTotal) * 100 : 0;
                  return (
                    <div key={method}>
                      <div className="flex justify-between text-xs mb-0.5">
                        <span className="text-muted-foreground">{t("bookings.payment_method_" + method, method)}</span>
                        <span className="text-foreground font-bold">{formatPrice(amount, "JOD")} <span className="text-muted-foreground/60 font-normal">({Math.round(pct)}%)</span></span>
                      </div>
                      <div className="h-1 bg-background rounded-sm overflow-hidden">
                        <div className="h-full bg-volt rounded-sm transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
